import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateCalendarComponent } from './date-calendar.component';
import { isoDateToDisplay, normalizeDisplayDate, toIsoDate } from './date-time-field.util';

@Component({
  selector: 'lib-date-field',
  standalone: true,
  imports: [DateCalendarComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFieldComponent),
      multi: true,
    },
  ],
  template: `
    <div class="date-field">
      <input
        [id]="inputId"
        class="date-field__input"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        [attr.aria-label]="ariaLabel"
        [placeholder]="placeholder"
        [value]="dateText"
        [disabled]="isDisabled"
        [readOnly]="readonly"
        (input)="onDateInput($event)"
        (blur)="onDateBlur()"
      />
      <button
        type="button"
        class="date-field__toggle"
        aria-label="Open calendar"
        [disabled]="isDisabled || readonly"
        (click)="toggleCalendar()"
      >
        <span class="date-field__icon" aria-hidden="true"></span>
      </button>
      @if (isCalendarOpen) {
        <div class="date-field__popover">
          <lib-date-calendar
            [selectedDate]="selectedIsoDate"
            [min]="min"
            [max]="max"
            (selectedDateChange)="selectDate($event)"
          />
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .date-field {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) 40px;
      align-items: stretch;
      width: 100%;
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      box-sizing: border-box;
    }

    .date-field__input {
      min-width: 0;
      border: none;
      border-radius: 8px 0 0 8px;
      padding: 0 12px;
      font: inherit;
      color: #111827;
      background: transparent;
      outline: none;
    }

    .date-field__toggle {
      display: grid;
      place-items: center;
      border: none;
      border-left: 1px solid #e5e7eb;
      border-radius: 0 8px 8px 0;
      background: #f8fafc;
      color: #475569;
      cursor: pointer;
    }

    .date-field__icon {
      position: relative;
      width: 15px;
      height: 14px;
      border: 1.5px solid currentColor;
      border-radius: 3px;
      box-sizing: border-box;
    }

    .date-field__icon::before {
      content: '';
      position: absolute;
      top: 3px;
      left: -1.5px;
      right: -1.5px;
      border-top: 1.5px solid currentColor;
    }

    .date-field__input:disabled,
    .date-field__input[readonly],
    .date-field__toggle:disabled {
      opacity: 0.65;
      cursor: default;
    }

    .date-field__popover {
      position: absolute;
      z-index: 40;
      top: calc(100% + 6px);
      left: 0;
    }
  `],
})
export class DateFieldComponent implements ControlValueAccessor {
  @Input() public inputId = `date-field-${Math.random().toString(36).slice(2, 10)}`;
  @Input() public placeholder = 'dd.mm.yyyy';
  @Input() public ariaLabel = 'Date';
  @Input() public readonly = false;
  @Input() public min: string | null = null;
  @Input() public max: string | null = null;

  protected dateText = '';
  protected isDisabled = false;
  protected isCalendarOpen = false;

  protected get selectedIsoDate(): string {
    return toIsoDate(this.dateText) ?? '';
  }

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  public writeValue(value: string | null | undefined): void {
    this.dateText = isoDateToDisplay(value);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected onDateInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.dateText = target?.value ?? '';
    this.emitValue();
  }

  protected onDateBlur(): void {
    this.dateText = normalizeDisplayDate(this.dateText);
    const clamped = this.clampDate(toIsoDate(this.dateText));
    if (clamped) {
      this.dateText = isoDateToDisplay(clamped);
    }
    this.onTouched();
    this.emitValue();
  }

  protected toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  protected selectDate(value: string): void {
    this.dateText = isoDateToDisplay(value);
    this.isCalendarOpen = false;
    this.onTouched();
    this.emitValue();
  }

  private emitValue(): void {
    const value = this.clampDate(toIsoDate(this.dateText));
    this.onChange(value ?? '');
  }

  private clampDate(value: string | null): string | null {
    if (!value) {
      return null;
    }

    if (this.min && value < this.min) {
      return this.min;
    }

    if (this.max && value > this.max) {
      return this.max;
    }

    return value;
  }
}
